import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import { toast } from "react-toastify";
import api from "../utils/api";

// Create WebSocket context
const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const { token, isAuthenticated, updateUserData } = useAuth();
  const { language } = useLanguage();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  // Add state for tracking order updates
  const [orderUpdateTrigger, setOrderUpdateTrigger] = useState(0);

  const fetchUser = async () => {
    try {
      // Real API call to get user subscriptions
      const response = await api.get("/users/Me");

      updateUserData(response.data.data);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    }
  };

  // Toast configuration function
  const getToastConfig = (type) => {
    switch (type) {
      case "error":
        return { autoClose: false };
      case "warning":
        return { autoClose: 8000 };
      case "success":
        return { autoClose: 3000 };
      case "info":
      default:
        return { autoClose: 5000 };
    }
  };

  // Connect to socket
  useEffect(() => {
    if (!isAuthenticated || !token) {
      return;
    }

    const newSocket = io("https://api.houfalmanazel.com");

    newSocket.on("connect", () => {
      console.log("Socket connected with ID:", newSocket.id);
      setSocketId(newSocket.id);
      setIsConnected(true);

      if (token) {
        newSocket.emit("authenticate", token);
        console.log("Authentication token sent");
      }
    });

    newSocket.on("disconnect", () => {
      console.log("Socket disconnected");
      setIsConnected(false);
      setSocketId(null);
    });

    newSocket.on("authenticated", (message) => {
      console.log("Socket authenticated:", message);
      toast.success(
        language === "en" ? "Successfully connected" : "تم الاتصال بنجاح",
        getToastConfig("success")
      );
    });

    newSocket.on("error", (message) => {
      console.error("Socket error:", message);
      toast.error(
        language === "en"
          ? `Connection error: ${message}`
          : `خطأ في الاتصال: ${message}`,
        getToastConfig("error")
      );
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        console.log("Disconnecting socket");
        newSocket.disconnect();
      }
    };
  }, [isAuthenticated, token, language]);

  // Set up business logic event handlers
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (message) => {
      console.log("Notification received:", message);

      let title = message.title || {};
      let text = message.text || {};

      const displayTitle =
        title[language] ||
        title[language === "en" ? "ar" : "en"] ||
        "New notification";
      const displayText =
        text[language] || text[language === "en" ? "ar" : "en"] || "";

      setNotifications((prev) => [
        ...prev,
        {
          ...message,
          displayTitle,
          displayText,
          read: false,
          timestamp: new Date().toISOString(),
        },
      ]);

      toast.info(displayTitle, getToastConfig("info"));
      fetchUser(); // Fetch user data after receiving a notification
    };

    const handleBookingSuccess = (message) => {
      console.log("Booking success:", message);
      toast.success(
        language === "en"
          ? message.text?.en || "Booking successful"
          : message.text?.ar || "تم الحجز بنجاح",
        getToastConfig("success")
      );

      // Trigger orders refresh
      setOrderUpdateTrigger((prev) => prev + 1);
    };

    const handleCancelBooking = (message) => {
      console.log("Booking canceled:", message);
      toast.warning(
        language === "en"
          ? message.text?.en || "Booking canceled"
          : message.text?.ar || "تم إلغاء الحجز",
        getToastConfig("warning")
      );

      // Trigger orders refresh
      setOrderUpdateTrigger((prev) => prev + 1);
    };

    const handleUpdateBooking = (message) => {
      console.log("Booking Update:", message);
      toast.info(
        language === "en"
          ? message.text?.en || "Booking updated"
          : message.text?.ar || "تم تحديث الحجز",
        getToastConfig("info")
      );

      // Trigger orders refresh - this is the main addition
      setOrderUpdateTrigger((prev) => prev + 1);
    };

    const handlePaymentRefund = (data) => {
      toast.info(
        language === "en"
          ? data.text?.en || "Payment refunded"
          : data.text?.ar || "تم استرداد المبلغ",
        getToastConfig("warning")
      );

      // Trigger orders refresh
      setOrderUpdateTrigger((prev) => prev + 1);
    };

    // Register event listeners
    socket.on("send-notification", handleNotification);
    socket.on("booking-success", handleBookingSuccess);
    socket.on("cancel-booking", handleCancelBooking);
    socket.on("update-booking", handleUpdateBooking);
    socket.on("paymentRefund", handlePaymentRefund);

    return () => {
      socket.off("send-notification", handleNotification);
      socket.off("booking-success", handleBookingSuccess);
      socket.off("cancel-booking", handleCancelBooking);
      socket.off("update-booking", handleUpdateBooking);
      socket.off("paymentRefund", handlePaymentRefund);
    };
  }, [socket, language]);

  // Manual disconnect function
  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setIsConnected(false);
      setSocketId(null);
    }
  }, [socket]);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Mark notification as read
  const markNotificationAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId ||
        notification.notificationId === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
  }, []);

  // Get unread notifications count
  const getUnreadCount = useCallback(() => {
    return notifications.filter((notification) => !notification.read).length;
  }, [notifications]);

  return (
    <WebSocketContext.Provider
      value={{
        socket,
        isConnected,
        socketId,
        notifications,
        clearNotifications,
        markNotificationAsRead,
        getUnreadCount,
        disconnect,
        orderUpdateTrigger, // Expose the trigger for components to listen to
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
